import React, { useCallback, useEffect, useRef, useState } from 'react'
import { Link, useLocation, useParams } from 'react-router-dom'
import { lmsAdmin, readFailureText } from '../api'
import { relativeTime } from '../format'
import { FleetPage, FleetSection, Notice } from '../../../shared/sso-workspace/fleet-page'
import { LocalTime } from '../../../shared/date/local-time-view'
import { utcText } from '../../../shared/date/local-time'
import '../lms-admin.scss'
import { LmsTrail, lmsTrail } from '../lms-trail'
import { useAuthContext } from '../../../context/auth.context'
import { useRoleOrg } from '../../../role/hook/role-org'
import { ROLE_ORG } from '../../../role/role.context'
import { ResendDialog } from '../resend-dialog'
import { resultRuleLabel } from '../result-rule'
import { LmsSentDetails } from './lms-sent-details'
import { SECTION } from '../../../shared/navigation/sections'
import { IconApp } from '../../../shared/icon/icon'

// Session Detail (docs/08 §20, design edxr-session-detail-v1.0.html).
// Drill-down from Active Sessions: state timeline (row timestamps +
// audit entries), writeback result, device/claim info. The artifact's
// per-attempt writeback retry log (Retrying / Giving up, force-retry)
// has no v1 backing — writeback happens once on completion and v1
// stores only its result (deviations D-047).
//
// It is a standard admin page (owner feedback 2026-10-20: a fixed-width card
// with an X did not follow the rest of the admin): FleetPage's pane, the title
// with the session's state, a back link to Sessions, and the sections full width.
//
// "The result for your LMS" is the session's writeback: the lesson status, score
// and time the service stored when the learner finished (session_status,
// session_score, session_duration_sec on the session this page already reads),
// and when that was recorded, which is the session's completion in its timeline.
// Whether the LMS has it is the service's written_back_at (review M1): a time
// (title "Sent <time>"), null (the service has not been told the LMS took it:
// "Not sent to your LMS yet"), or no such key at all (a service older than the
// field, and then the page claims nothing: "The result for your LMS"). The page
// says "Recorded" for the completion, never "Received".

// The session's states (rapid-api-lms domain/session/agg.go). A launch is
// linked in one step since Phase F, but one from before it may still wait in
// pending_link_confirmation.
const STATE_PILLS = {
  unlinked: { tone: 'info', label: 'Waiting for account link' },
  pending_link_confirmation: { tone: 'info', label: 'Waiting for account link' },
  ready: { tone: 'info', label: 'Ready' },
  claimed: { tone: 'warn', label: 'Claimed' },
  in_progress: { tone: 'ok', label: 'In progress' },
  completed: { tone: 'ok', label: 'Completed' },
  failed: { tone: 'bad', label: 'Failed' },
  rejected: { tone: 'bad', label: 'Rejected' },
  expired: { tone: null, label: 'Expired' }
}

const ACRONYMS = { lti: 'LTI', lms: 'LMS', scorm: 'SCORM', xapi: 'xAPI', cmi5: 'cmi5' }

const humanize = state =>
  (state || '')
    .replace(/[._]/g, ' ')
    .replace(/^\w/, c => c.toUpperCase())
    .replace(/\b(lti|lms|scorm|xapi|cmi5)\b/gi, word => ACRONYMS[word.toLowerCase()])

const fmtDuration = seconds => {
  if (seconds === undefined || seconds === null) return '—'
  const m = Math.floor(seconds / 60)
  const s = seconds % 60
  return m ? `${m}m ${s}s` : `${s}s`
}

// The lesson status the LMS was sent, in words (passed -> Passed, not attempted
// -> Not attempted).
const lessonStatus = status => (status ? humanize(status) : '—')

// The completion in the session's timeline: the last one, which is the
// session's own (the service adds it after the events of the audit trail).
const completedAt = timeline => {
  const done = [...timeline].reverse().find(entry => entry.state === 'completed')
  return done ? done.at : null
}

// Whether the session has anything to send: a lesson status, a score or a time.
const hasResult = session =>
  Boolean(session.session_status) ||
  (session.session_score !== null && session.session_score !== undefined) ||
  (session.session_duration_sec !== null && session.session_duration_sec !== undefined)

// A session still to be linked or played has not sent its result; one that ended
// without one never will.
const PENDING = ['unlinked', 'pending_link_confirmation', 'ready', 'claimed', 'in_progress']

// Whether the service said anything about the LMS having the result: its
// written_back_at key, a time or null. An older service sends no such key.
const saysWrittenBack = session => Object.prototype.hasOwnProperty.call(session, 'written_back_at')

// A SCORM learner's newest completed result that the LMS has not taken goes with
// the learner's next launch of the course (the service's resync). One that ended
// incomplete is not a result and is never carried (domain/session/results-pg.go).
const carriedToNextLaunch = session => session.lms_user_id_kind === 'scorm' && session.state === 'completed' && session.session_status !== 'incomplete'

// The LTI grade the LMS was actually given: the newest sent grade among the
// timeline's deliveries (channel "lti"). It is the course's rule's pick, which
// may be another session's result than this session's own.
const sentGrade = timeline => {
  const sent = timeline.filter(entry => entry.delivery && entry.delivery.channel === 'lti' && entry.delivery.status === 'sent' && entry.delivery.grade)
  return sent.length ? sent[sent.length - 1].delivery : null
}

const gradeText = grade => {
  const given = grade.grade.score_given
  const max = grade.grade.score_maximum
  if (given === undefined || given === null) return '—'
  return max === undefined || max === null ? `${given}` : `${given} out of ${max}`
}

// The mode the LMS opened the course in: the session's own lms_mode and
// lms_credit (the first the launcher reported), else the timeline's report of
// the LMS's record (lms_read). Browse, review or no credit are never recorded
// for the learner, so the service keeps the result and never sends it.
const noCreditMode = (session, timeline) => {
  if (session.lms_mode === 'review' || session.lms_mode === 'browse') return session.lms_mode
  if (session.lms_credit === false) return 'no-credit'
  if (session.lms_mode || session.lms_credit === true) return null
  for (const entry of timeline) {
    const details = entry.details
    if (!details || typeof details !== 'object' || !/lms_read$/.test(entry.state || '')) continue
    if (details.mode === 'review' || details.mode === 'browse') return details.mode
    if (details.credit === false) return 'no-credit'
  }
  return null
}

const MODE_WORDS = { review: 'review mode', browse: 'browse mode', 'no-credit': 'no-credit mode' }

const hasScore = session => session.session_score !== null && session.session_score !== undefined

// What the section is titled, and what it says under the title, for each of the
// things the service can say.
const writebackWords = (session, timeline) => {
  const sentAt = session.written_back_at
  const grade = sentGrade(timeline)
  if (sentAt || grade) {
    return {
      title: (
        <>
          Sent <LocalTime value={sentAt || grade.sent_at} seconds />
        </>
      ),
      lede: grade
        ? "The grade your LMS was given is the course's rule's pick, which may be another session's. The rest is what this session recorded."
        : 'What this session recorded. The timeline shows what your LMS was given.'
    }
  }
  if (saysWrittenBack(session) && hasResult(session)) {
    const mode = noCreditMode(session, timeline)
    if (mode) {
      return {
        title: 'Kept in EdXR, not sent to your LMS',
        kept: true,
        lede: `Your LMS opened the course in ${MODE_WORDS[mode]}, so the result stays in EdXR and is not sent.`
      }
    }
    let lede = null
    let title = 'Not sent to your LMS yet'
    if (session.lms_user_id_kind === 'lti13' && !hasScore(session)) {
      title = 'No grade sent'
      lede = 'No grade sent: this result has no score.'
    } else if (carriedToNextLaunch(session)) lede = "The learner's next launch of this course sends it, unless the course's rule picks another result."
    else if (session.lms_user_id_kind === 'lti13') {
      lede = (
        <>
          A grade that could not be sent is listed on <Link to={`/org/lms/connections/${session.connection_id}`}>the connection's page</Link>, where it can be sent again.
        </>
      )
    }
    return { title, lede }
  }
  return { title: 'The result for your LMS', lede: "What the learner's session recorded when they finished the course." }
}

// What a resend did, in words, from the service's answer. The course's rule
// picks the result across the learner's sessions, which may be another
// session's (`result_of`): then that session is named and linked.
const resendOutcome = sessionRef => result => {
  if (result.nothing_to_resend) return 'There was nothing to resend for this session.'
  const rule = resultRuleLabel(result.result_rule)
  const pick = (ref, tail) => (
    <>
      The course&apos;s rule{rule ? ` (${rule})` : ''} picks the result of session{' '}
      <Link to={`/org/lms/sessions/${ref}`} data-testid="resend-result-of">
        {ref}
      </Link>
      ; {tail}
    </>
  )
  const lines = []
  if (rule) lines.push(<>Rule: {rule}.</>)
  const lti = result.lti
  if (lti) {
    if (lti.requeued) {
      lines.push('The grade was queued to be posted again.')
      if (lti.result_of && lti.result_of !== sessionRef) lines.push(pick(lti.result_of, 'that is what was sent.'))
    } else if (lti.note) lines.push(lti.note)
  }
  const statements = result.statements && result.statements.requeued
  if (statements > 0) lines.push(`${statements} ${statements === 1 ? 'statement' : 'statements'} queued to be sent to the record store again.`)
  const scorm = result.scorm
  if (scorm && (scorm.superseded || scorm.pending_resync)) {
    if (scorm.result_of && scorm.result_of !== sessionRef) lines.push(pick(scorm.result_of, "it will be written at the learner's next launch."))
    else if (scorm.superseded) lines.push("This session's result is not the one the course's rule picks, so it will not be written.")
    else lines.push("The result will be written to the LMS at the learner's next launch.")
  }
  if (!lines.length || (lines.length === 1 && rule)) return 'The result was queued to be sent again.'
  return lines.map((line, i) => (
    <React.Fragment key={i}>
      {i > 0 ? ' ' : null}
      {line}
    </React.Fragment>
  ))
}

const RESEND_INTRO =
  "The grade is posted again, the record store gets the statements again without duplicates, and a SCORM result is written at the learner's next launch. The reason is kept in the audit log."
const IMPERSONATING_NOTE = "You're signed in as someone else, so a result can't be resent. End the impersonation to resend."

// "Resend result": for the LMS admin scope (the service checks it too), never
// while impersonating (shown disabled with the reason).
const ResendResult = ({ sessionRef, reload }) => {
  const lms = useRoleOrg(ROLE_ORG.LMS)
  const { state } = useAuthContext()
  const [open, updateOpen] = useState(false)
  const sent = useRef(false)
  if (!(lms.accessCreate && lms.accessDelete)) return null
  const impersonating = !!(state && state.jwtClaims && state.jwtClaims.impersonationEndsAt)
  return (
    <>
      <button
        className="btn btn-outline-secondary btn-sm"
        type="button"
        disabled={impersonating}
        title={impersonating ? IMPERSONATING_NOTE : undefined}
        data-testid="session-resend"
        onClick={() => updateOpen(true)}
      >
        Resend result
      </button>
      {impersonating && (
        <span className="sw-hint" data-testid="session-resend-note">
          {IMPERSONATING_NOTE}
        </span>
      )}
      {open && (
        <ResendDialog
          testId="session-resend-dialog"
          title="Resend result"
          intro={RESEND_INTRO}
          sendLabel="Resend result"
          failureFallback="The result couldn't be resent."
          send={reason => lmsAdmin.resendSession(sessionRef, reason)}
          outcome={resendOutcome(sessionRef)}
          onDone={() => { sent.current = true }}
          onClose={() => {
            updateOpen(false)
            // The page is read again once the person has seen what was done.
            if (sent.current) { sent.current = false; reload() }
          }}
        />
      )}
    </>
  )
}

const DELIVERY_STATUS = {
  sent: { tone: 'ok', label: 'Sent' },
  pending: { tone: 'warn', label: 'Waiting' },
  failed: { tone: 'bad', label: 'Failed' }
}

// The delivery's facts under its label, always shown (the house pages keep no
// disclosure widgets): attempts, when it was sent, the answer, the last error.
const DeliveryDetails = ({ delivery, index }) => (
  <div className="sw-hint" style={{ flexBasis: '100%' }} data-testid={`delivery-details-${index}`}>
    <span data-testid={`delivery-attempts-${index}`}>
      {delivery.attempts === undefined ? 'Attempts —' : `${delivery.attempts} ${delivery.attempts === 1 ? 'attempt' : 'attempts'}`}
    </span>
    {' · '}
    {delivery.sent_at ? (
      <>
        Sent <LocalTime value={delivery.sent_at} seconds />
      </>
    ) : (
      'Not sent yet'
    )}
    {delivery.answer_code !== undefined && delivery.answer_code !== null && (
      <>
        {' · '}
        <span data-testid={`delivery-answer-${index}`}>Answer {delivery.answer_code}</span>
      </>
    )}
    {delivery.last_error && (
      <>
        {' · '}
        <span data-testid={`delivery-error-${index}`}>{delivery.last_error}</span>
      </>
    )}
  </div>
)

const WritebackSection = ({ session, timeline, reload }) => {
  const recorded = completedAt(timeline)
  const words = writebackWords(session, timeline)
  const grade = sentGrade(timeline)
  return (
    <FleetSection id="session-writeback" title={words.title} lede={words.lede} actions={words.kept ? null : <ResendResult sessionRef={session.ref} reload={reload} />}>
      {session.resync_pending && (
        <p className="sw-hint" data-testid="resync-pending">
          Waiting to be written to the LMS at the learner's next launch.
        </p>
      )}
      {session.resync_superseded && (
        <p className="sw-hint" data-testid="resync-superseded">
          This is not the result the course's rule picks.
        </p>
      )}
      {hasResult(session) ? (
        <dl className="sw-dl">
          {grade && (
            <>
              <dt>Grade sent</dt>
              <dd data-testid="writeback-grade-sent">{gradeText(grade)}</dd>
            </>
          )}
          <dt>{grade ? "This session's status" : 'Lesson status'}</dt>
          <dd data-testid="writeback-status">{lessonStatus(session.session_status)}</dd>
          <dt>{grade ? "This session's score" : 'Score'}</dt>
          {/* session_score is SCORM raw scale (0-100), not a fraction. */}
          <dd data-testid="writeback-score">{session.session_score === null || session.session_score === undefined ? '—' : `${session.session_score}%`}</dd>
          <dt>Session time</dt>
          <dd data-testid="writeback-time">{fmtDuration(session.session_duration_sec)}</dd>
          <dt>Recorded</dt>
          <dd data-testid="writeback-recorded">{recorded ? <LocalTime value={recorded} seconds /> : '—'}</dd>
        </dl>
      ) : (
        <p className="sw-hint m-0">
          {PENDING.includes(session.state) ? 'Nothing has been sent to your LMS yet.' : 'Nothing was sent to your LMS for this session.'}
        </p>
      )}
    </FleetSection>
  )
}

const eventCount = n => `${n} ${n === 1 ? 'event' : 'events'}`

// Where the back link goes (review L2): the list the person opened the session
// from (an opener passes its address, query included, as location.state.from),
// else Active sessions, the list the page is a drill-down of. Only this
// section's lists are taken, by name, never an address from anywhere else.
// The trail names the same list by its sidebar entry, and its crumb returns
// to it as the back link does (audit F4).
const BACK_TARGETS = {
  '/org/lms/activity': { label: 'Activity', section: SECTION.lmsActivity },
  '/org/lms/sessions': { label: 'Active sessions', section: SECTION.lmsSessions }
}
const DEFAULT_BACK = { to: '/org/lms/sessions', label: 'Active sessions', crumb: SECTION.lmsSessions }

const backTarget = from => {
  if (typeof from !== 'string' || !from.startsWith('/') || from.startsWith('//')) return DEFAULT_BACK
  const target = BACK_TARGETS[from.split(/[?#]/)[0]]
  return target ? { to: from, label: target.label, crumb: { ...target.section, url: from } } : DEFAULT_BACK
}

export const ViewSessionDetail = () => {
  const { sessionRef } = useParams()
  const location = useLocation()
  const back = backTarget(location.state && location.state.from)

  const [session, updateSession] = useState(null)
  const [error, updateError] = useState(null)

  const load = useCallback(() => lmsAdmin.getSession(sessionRef).then(updateSession).catch(updateError), [sessionRef])
  useEffect(() => {
    load()
  }, [load])

  const pill = session && (STATE_PILLS[session.state] || { tone: null, label: humanize(session.state) })
  const created = session && relativeTime(session.created_at)
  const timeline = (session && session.state_timeline) || []

  const title = (
    <>
      {session && session.course ? session.course.name : 'Session'}
      {pill && (
        <span className={['sw-pill', pill.tone, 'ms-2', 'align-middle'].filter(Boolean).join(' ')} data-testid="session-state">
          {pill.label}
        </span>
      )}
    </>
  )
  const purpose = session ? (
    <>
      {session.edxr_user_email || 'Not yet linked'}
      {created && (
        <>
          {' · '}
          <span title={utcText(session.created_at)}>launched {created.text}</span>
        </>
      )}
    </>
  ) : null

  return (
    <FleetPage testId="session-detail" title={title} purpose={purpose}>
      <LmsTrail
        trail={lmsTrail(
          back.crumb,
          { url: `/org/lms/sessions/${sessionRef}`, text: session && session.course ? session.course.name : 'Session', Icon: IconApp }
        )}
      />
      <p className="mb-2">
        <Link to={back.to} data-testid="session-back">
          ← {back.label}
        </Link>
      </p>

      {error && (
        <Notice tone="bad" role="alert">
          {error.code === 'NOT_FOUND' ? 'Session not found.' : readFailureText('The session', error)}
        </Notice>
      )}
      {!session && !error && (
        <p className="sw-hint" role="status">
          Loading…
        </p>
      )}

      {session && (
        <>
          <WritebackSection session={session} timeline={timeline} reload={load} />

          <FleetSection id="session-details" title="Details">
            <dl className="sw-dl">
              <dt>Session ref</dt>
              <dd className="sw-mono">{session.ref}</dd>
              <dt>LMS user</dt>
              <dd className="sw-mono">{session.lms_user_id || '—'}</dd>
              <dt>Connection</dt>
              <dd>#{session.connection_id}</dd>
              <dt>Device</dt>
              <dd>{session.device_label || '—'}</dd>
              <dt>Claimed via</dt>
              <dd>
                {session.claim_method || '—'}
                {session.claim_source && <span className="sw-hint ms-2">{session.claim_source}</span>}
              </dd>
            </dl>
          </FleetSection>

          <FleetSection id="session-timeline" title="State timeline" lede={eventCount(timeline.length)}>
            <ol className="lms-timeline">
              {timeline.map((entry, index) => (
                <li key={`${entry.state}-${index}`} data-testid={`timeline-${index}`}>
                  <span className="lms-timeline-state">
                    {entry.label || humanize(entry.state)}
                    {entry.delivery && (
                      <span
                        className={['sw-pill', (DELIVERY_STATUS[entry.delivery.status] || {}).tone, 'ms-2'].filter(Boolean).join(' ')}
                        data-testid={`delivery-status-${index}`}
                      >
                        {(DELIVERY_STATUS[entry.delivery.status] || { label: humanize(entry.delivery.status) }).label}
                      </span>
                    )}
                  </span>
                  <LocalTime value={entry.at} seconds />
                  {entry.delivery && <DeliveryDetails delivery={entry.delivery} index={index} />}
                  <LmsSentDetails entry={entry} index={index} />
                </li>
              ))}
            </ol>
            {session.deliveries_truncated && (
              <p className="sw-hint mt-2 mb-0" data-testid="deliveries-truncated">
                Older deliveries not shown
              </p>
            )}
          </FleetSection>
        </>
      )}
    </FleetPage>
  )
}
